import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Plus, Minus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useB2BAuth } from '../../context/B2BAuthContext';
import { useB2BOrders } from '../../context/B2BOrderContext';
import { products, businessProducts, businessTypes } from '../../data/b2bData';

const B2BOrder = () => {
  const navigate = useNavigate();
  const { type } = useParams();
  const { user, isAuthenticated } = useB2BAuth();
  const { createOrder } = useB2BOrders();
  const [orderItems, setOrderItems] = useState({});
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/b2b/login');
    }
  }, [isAuthenticated, navigate]);

  const businessType = businessTypes.find(t => t.id === type);
  const availableProducts = businessProducts[type] || [];

  const getProduct = (productId) => products.find(p => p.id === productId);

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      const newItems = { ...orderItems };
      delete newItems[productId];
      setOrderItems(newItems);
    } else {
      setOrderItems(prev => ({ ...prev, [productId]: quantity }));
    }
  };

  const handleQuickSelect = (productId, quantity) => {
    setOrderItems(prev => ({ ...prev, [productId]: quantity }));
  };

  const calculateTotal = () => {
    return Object.entries(orderItems).reduce((total, [productId, quantity]) => {
      const product = getProduct(productId);
      return total + (product?.price || 0) * quantity;
    }, 0);
  };

  const getOrderItemsArray = () => {
    return Object.entries(orderItems)
      .filter(([_, quantity]) => quantity > 0)
      .map(([productId, quantity]) => {
        const product = getProduct(productId);
        return {
          productId,
          productName: product.name,
          quantity,
          unit: product.unit,
          unitPrice: product.price,
          totalPrice: product.price * quantity
        };
      });
  };

  const handleSubmitOrder = () => {
    const items = getOrderItemsArray();
    if (items.length === 0) return;

    const order = createOrder({
      userId: user.id,
      customerName: user.fullName,
      businessName: user.businessName,
      businessType: type,
      items,
      totalAmount: calculateTotal()
    });

    navigate(`/b2b/order-confirm/${order.id}`);
  };

  const totalItems = Object.values(orderItems).reduce((a, b) => a + b, 0);
  const totalAmount = calculateTotal();

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/b2b/dashboard')}
              className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-bold text-gray-900">طلب جديد</h1>
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <span>{businessType?.icon}</span>
                {businessType?.name}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Products List */}
        <div className="space-y-4">
          {availableProducts.map(({ productId, quickSelect }) => {
            const product = getProduct(productId);
            if (!product) return null;

            const quantity = orderItems[productId] || 0;

            return (
              <div
                key={productId}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
              >
                <div className="flex items-start gap-4">
                  {/* Product Icon */}
                  <div className="w-14 h-14 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl">{product.image}</span>
                  </div>

                  {/* Product Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-gray-900">{product.name}</h3>
                        <p className="text-sm text-gray-500">
                          {product.unitDescription || `الوحدة: ${product.unit}`}
                        </p>
                      </div>
                      <span className="font-bold text-green-600">
                        €{product.price.toFixed(2)}/{product.unit}
                      </span>
                    </div>

                    {/* Quick Select Buttons */}
                    {quickSelect.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {quickSelect.map((num) => (
                          <button
                            key={num}
                            onClick={() => handleQuickSelect(productId, num)}
                            className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                              quantity === num
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(productId, quantity - 1)}
                        className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-gray-200 transition-colors"
                        disabled={quantity === 0}
                      >
                        {quantity === 1 ? <Trash2 className="w-4 h-4 text-red-500" /> : <Minus className="w-4 h-4" />}
                      </button>
                      <input
                        type="number"
                        value={quantity || ''}
                        onChange={(e) => updateQuantity(productId, parseInt(e.target.value) || 0)}
                        className="w-20 text-center py-2 border border-gray-200 rounded-xl font-bold text-lg"
                        placeholder="0"
                        min="0"
                      />
                      <button
                        onClick={() => updateQuantity(productId, quantity + 1)}
                        className="w-10 h-10 bg-green-100 text-green-700 rounded-xl flex items-center justify-center hover:bg-green-200 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      {quantity > 0 && (
                        <span className="text-green-600 font-bold mr-auto">
                          €{(product.price * quantity).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Bottom Bar */}
      {totalItems > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-gray-500 text-sm">الإجمالي</p>
                <p className="text-2xl font-bold text-gray-900">€{totalAmount.toFixed(2)}</p>
              </div>
              <div className="text-left">
                <p className="text-gray-500 text-sm">عدد الأصناف</p>
                <p className="font-bold text-gray-900">{Object.keys(orderItems).filter(k => orderItems[k] > 0).length} صنف</p>
              </div>
            </div>
            <Button
              onClick={handleSubmitOrder}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold rounded-xl"
            >
              <ShoppingCart className="w-5 h-5 ml-2" />
              مراجعة الطلب
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2BOrder;